/**
 * Registro do service worker.
 * A versao do arquivo muda a cada publicacao (o build carimba um hash do
 * conteudo), entao o navegador percebe a novidade e avisa a pessoa em vez de
 * continuar servindo a versao velha do cache.
 */
export function registrarServiceWorker(aoTerAtualizacao: (aplicar: () => void) => void): void {
  if (!('serviceWorker' in navigator)) return;
  if (location.protocol !== 'https:' && location.hostname !== 'localhost') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registro) => {
        registro.addEventListener('updatefound', () => {
          const novo = registro.installing;
          if (!novo) return;
          novo.addEventListener('statechange', () => {
            // ja existe uma versao rodando e chegou outra: da para avisar
            if (novo.state === 'installed' && navigator.serviceWorker.controller) {
              aoTerAtualizacao(() => {
                novo.postMessage('ATUALIZAR_AGORA');
              });
            }
          });
        });
        // confere se tem versao nova de tempos em tempos
        setInterval(() => void registro.update().catch(() => undefined), 30 * 60 * 1000);
      })
      .catch((e) => {
        // ARMADILHA 12: falha de registro aparece no console, nao some
        console.error('[pwa] não consegui registrar o service worker', e);
      });

    let recarregando = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (recarregando) return;
      recarregando = true;
      location.reload();
    });
  });
}

/* ------------------------- convite de instalacao ------------------------- */

type ConviteDeInstalacao = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let convite: ConviteDeInstalacao | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((o) => o());

/**
 * O navegador dispara o convite uma vez, no carregamento, e nao repete. Por isso
 * a escuta fica aqui no modulo e nao dentro de um componente: quando a tela
 * monta, o evento ja passou.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // sem o preventDefault o Chrome mostra a barra dele, que a pessoa fecha sem ler
    e.preventDefault();
    convite = e as ConviteDeInstalacao;
    avisar();
  });

  window.addEventListener('appinstalled', () => {
    convite = null;
    avisar();
  });
}

/** Ja esta rodando como app instalado, sem a barra do navegador. */
export function estaInstalado(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone e iPad nao tem o convite automatico: la a instalacao e pelo Compartilhar. */
export function ehIOS(): boolean {
  const ua = navigator.userAgent;
  const iPadNovo = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPadNovo;
}

export function temConvite(): boolean {
  return convite !== null;
}

export function assinarConvite(aoMudar: () => void): () => void {
  ouvintes.add(aoMudar);
  return () => ouvintes.delete(aoMudar);
}

/** Abre o convite do navegador. Devolve o que a pessoa escolheu. */
export async function instalar(): Promise<'aceito' | 'recusado' | 'sem_convite'> {
  if (!convite) return 'sem_convite';
  const atual = convite;
  await atual.prompt();
  const escolha = await atual.userChoice;
  // o convite so serve uma vez: guardar depois disso daria botao que nao faz nada
  convite = null;
  avisar();
  return escolha.outcome === 'accepted' ? 'aceito' : 'recusado';
}
